<script>
import "@/assets/scss/mixins.scss";
import { CUSTOMER } from "@/environment/env.customer";

export default {
  name: "DefaultLayout",
  props: {
    customer: {
      type: Object,
      default: () => CUSTOMER,
    },
    pageProps: { type: Object, required: true },
  },
};
</script>

<template>
  <div class="layout-wrapper">
    <div class="background-gradient"></div>
    <div class="container">
      <!--  Left section  -->
      <div class="page-content">
        <div class="left-side-header">
          <img
            alt="customer logo"
            class="logo"
            :src="customer.customerLogoLink"
          />
        </div>
        <div class="left-side-content">
          <div class="title">
            {{ customer.leftSideContent.title }}
          </div>
          <div class="description">
            {{ customer.leftSideContent.description }}
          </div>

          <div class="ticks">
            <div
              v-for="tick in customer.leftSideContent.ticks"
              :key="tick"
              class="tick"
            >
              <div class="svg-wrapper">
                <svg
                  width="14px"
                  height="14px"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 640"
                >
                  <!--!Font Awesome Pro v7.1.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) Copyright 2025 Fonticons, Inc.-->
                  <path
                    fill="currentColor"
                    d="M530.8 134.1C545.1 144.5 548.3 164.5 537.9 178.8L281.9 530.8C276.4 538.4 267.9 543.1 258.5 543.9C249.1 544.7 240 541.2 233.4 534.6L105.4 406.6C92.9 394.1 92.9 373.8 105.4 361.3C117.9 348.8 138.2 348.8 150.7 361.3L252.2 462.8L486.2 141.1C496.6 126.8 516.6 123.6 530.9 134z"
                  />
                </svg>
              </div>
              <div>{{ tick }}</div>
            </div>
          </div>
        </div>
        <div class="footer" v-if="customer.leftSideContent.footerContent">
          <div class="line"></div>
          {{ customer.leftSideContent.footerContent }}
        </div>
      </div>
      <!--  Right section  -->
      <div class="page-content">
        <div class="right-side-placeholder"></div>
        <main>
          <div class="right-side-page-props">
            <div class="page-title">
              {{ pageProps.title }}
            </div>

            <div class="page-description">
              {{ pageProps.description }}
            </div>
          </div>
          <slot name="content" />
        </main>
        <div class="right-side-footer">
          <span class="footer-content">
            {{ customer.rightSideContent.footerContent }}
          </span>
          <div class="powered-by">
            {{ customer.rightSideContent.poweredByContent }}
            <a href="https://www.intheloop.io/">intheloop.io</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.layout-wrapper {
  position: relative;
}
.container {
  --theme-spacing: 16px;

  display: grid;
  justify-content: center;
  position: relative;
  width: 100vw;
  margin: 0 auto;
  min-height: 100vh;
  max-width: calc(728px + 2 * var(--padding-horizontal-page));
  padding: var(--padding-vertical-page, 0) var(--padding-horizontal-page, 0);
  --padding-vertical-page: var(--theme-spacing);
  z-index: 1;
  --padding-horizontal-page: calc(2 * var(--theme-spacing));

  @include media-sm-min() {
    --padding-horizontal-page: calc(4 * var(--theme-spacing));
    --padding-vertical-page: var(--padding-horizontal-page);
  }

  @include media-lg-min() {
    --padding-horizontal-page: calc(2 * var(--theme-spacing));
    --padding-vertical-page: var(--padding-horizontal-page);
    margin: 0;
    max-width: none;

    @media (max-height: 700px) {
      --padding-vertical-page: calc(
        var(--theme-spacing-half) + var(--theme-spacing)
      );
    }
  }

  @include media-xl-min() {
    --padding-horizontal-page: calc(4 * var(--theme-spacing));
    --content-max-w: 480px;

    @media (max-height: 700px) {
      --padding-vertical-page: calc(2 * var(--theme-spacing));
    }
  }

  @media (min-width: 1920px) {
    @media (max-height: 700px) {
      --padding-vertical-page: calc(3 * var(--theme-spacing));
    }
  }

  @include media-lg-min() {
    --page-section-max-width: calc(
      ((1920px / 2) - (var(--padding-horizontal-page) * 2))
    );
    grid-template-areas: none;
    grid-template-columns: minmax(0, var(--page-section-max-width)) minmax(
        0,
        var(--page-section-max-width)
      );
    column-gap: calc(2 * var(--padding-horizontal-page));
    max-height: 100vh;
    overflow: auto;
  }

  grid-template-areas:
    "header"
    "content"
    "left-content"
    "footer";
  grid-template-rows: repeat(2, auto) 1fr auto;
  grid-template-columns: minmax(0, 1fr);

  .page-content {
    display: contents;
    @include media-lg-min {
      display: grid;
      grid-row: 1 / 5;
      grid-template-rows: subgrid;
      grid-template-columns: subgrid;
    }
  }
}

.right-side-placeholder {
  @include media-lg-min() {
    grid-row: 1 / 2;
  }
}

.right-side-footer {
  grid-area: footer;
  align-self: flex-end;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @include media-lg-min() {
    grid-row: 4 / 5;
  }

  .footer-content {
    color: var(--theme-color-medium);
  }

  .powered-by {
    font-size: 12px;
    color: var(--theme-color-medium);
  }
}

.right-side-page-props {
  text-align: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0;

  .page-title {
    font-family: "DM Serif Display", Georgia, serif;
    font-size: 1.8rem;
    font-weight: 700;
  }

  .page-description {
    color: var(--theme-color-medium);
    font-size: 16px;
  }
}

.left-side-header {
  grid-area: header;

  @include media-lg-min() {
    grid-row: 1 / 2;
  }
}

.left-side-content {
  @include media-lg-max() {
    display: none;
  }
  grid-area: left-content;

  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 24px;
  color: var(--theme-color-primary-contrast);

  .title {
    font-family: "DM Serif Display", Georgia, serif;
    font-weight: 700;
    font-size: 3rem;
    line-height: 1;
  }

  .description {
    font-size: 1.25rem;
    opacity: 0.8;
  }

  .ticks {
    display: flex;
    flex-direction: column;
    gap: 16px;
    font-size: 1rem;
    opacity: 0.9;

    .tick {
      display: flex;
      gap: 8px;

      .svg-wrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px;
        border-radius: 50%;
        background-color: rgba(var(--theme-color-primary-contrast-rgb), 0.2);
      }
    }
  }

  @include media-lg-min() {
    grid-row: 3 / 4;
  }
}

@mixin content {
  width: 100%;
  max-width: var(--content-max-w);

  @include media-xl-min() {
    justify-self: center;
  }
}

main {
  @include content;
  grid-area: content;
  max-width: var(--content-max-w);
  align-self: center;

  @include media-lg-min() {
    grid-row: 3 / 4;
  }
}

.footer {
  color: var(--theme-color-primary-contrast);
  align-self: flex-start;
  opacity: 0.6;

  display: flex;
  align-items: center;
  gap: 16px;

  .line {
    flex: 1;
    max-width: 100px;
    background-color: var(--theme-color-primary-contrast);
    height: 0.5px;
  }

  @include media-lg-min() {
    align-self: flex-end;
    grid-row: 4 / 5;
  }
}

.background-gradient {
  @include media-lg-min() {
    width: 50vw;
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0) 50%),
      var(--theme-color-primary);
  }
}

.logo {
  height: 40px;

  @include media-lg-min() {
    height: 60px;
  }
}
</style>
